/**
 * Propose additional work discovered mid-job ("wheel off — brakes shot").
 * Creates a PENDING_APPROVAL SERVICE, checklist expanded, that the manager
 * clears with the customer (POST /technical/work-orders/{id}/services/propose).
 * Optionally reference a priced catalog service so the estimate reflects it.
 *
 * A service is proposed, never a task: the customer is asked about the job,
 * not about its steps. Asking step by step is what let a job end up
 * half-approved — which is why the old `serviceTypeIds[]` array is now one
 * optional `serviceTypeId`.
 */
import { useEffect, useState } from 'react'
import { Button, Modal } from '@vollgas/shared/ui'
import { serviceLabel } from '@/apis/staffApis'
import { useI18n } from '@/i18n/TranslationContext'
import { useCatalogServices } from '@/hooks/useStaff'
import { useProposeService } from '@/hooks/useWorkshop'

interface Props {
  readonly open: boolean
  readonly workOrderId: string
  readonly onClose: () => void
}

export function ProposeServiceModal({ open, workOrderId, onClose }: Props) {
  const { t } = useI18n()
  const { data } = useCatalogServices()
  const propose = useProposeService()
  const services = (data?.content ?? []).filter((s) => s.isActive)

  const [title, setTitle] = useState('')
  const [note, setNote] = useState('')
  const [serviceId, setServiceId] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) { setTitle(''); setNote(''); setServiceId(''); setError(null) }
  }, [open])

  const submit = () => {
    setError(null)
    const trimmed = title.trim()
    if (!trimmed) { setError(t.proposeService.errorTitle); return }
    propose.mutate({
      workOrderId,
      body: {
        label: trimmed,
        note: note.trim() || null,
        serviceTypeId: serviceId || null,
      },
    }, {
      onSuccess: onClose,
      onError: () => setError(t.proposeService.errorSubmit),
    })
  }

  const FIELD = 'w-full rounded-sm border border-glass-border bg-glass-fill px-3 py-2 text-13 text-glass-text placeholder:text-glass-text-muted focus:border-accent focus:outline-none'

  return (
    <Modal open={open} onClose={onClose} closeLabel={t.common.close} labelledById="propose-title" size="md">
      <h2 id="propose-title" className="mb-1 text-lg font-extrabold text-glass-text">{t.proposeService.title}</h2>
      <p className="mb-3 text-13 text-glass-text-muted">{t.proposeService.hint}</p>

      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-12-5 font-semibold text-glass-text-muted">
          {t.proposeService.workTitle}
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t.proposeService.workTitlePlaceholder}
            autoFocus
            className={FIELD}
          />
        </label>
        <label className="flex flex-col gap-1 text-12-5 font-semibold text-glass-text-muted">
          {t.proposeService.service}
          <select value={serviceId} onChange={(e) => setServiceId(e.target.value)} className={FIELD}>
            <option value="">{t.proposeService.noService}</option>
            {services.map((s) => (
              <option key={s.id} value={s.id}>{serviceLabel(s)}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-12-5 font-semibold text-glass-text-muted">
          {t.proposeService.note}
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder={t.proposeService.notePlaceholder}
            className={`${FIELD} resize-none`}
          />
        </label>
      </div>

      {error && <p role="alert" className="mt-2 text-13 text-accent">{error}</p>}

      <div className="mt-4 flex gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded-sm border border-glass-border bg-transparent px-4 py-px11 text-13 font-semibold text-glass-text-muted hover:bg-glass-fill-strong hover:text-glass-text"
        >
          {t.common.cancel}
        </button>
        <Button variant="primary" size="lg" onClick={submit} loading={propose.isPending} className="flex-1">
          {t.proposeService.submit}
        </Button>
      </div>
    </Modal>
  )
}
